import React, { useEffect, useState } from "react";
import axios from "../api/baseurl";
import { useNavigate, useParams } from "react-router-dom";
import "./Spinner.css";
import "./Loader.css";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { BiSolidCheckCircle, BiSolidXCircle } from "react-icons/bi";
import UploadReport from "./uploadReport";
import TestForPackage from "./testsByPackagePopup";
import { FiInfo } from "react-icons/fi";
import { Pagination } from "react-bootstrap";
import DisclaimerPopup from "./disclaimerPopup";
import AppointmentReschedule from "./appointmentReschedule";
import { FaDownload, FaEdit, FaRedoAlt } from 'react-icons/fa';
import FamilyAppointmentCancel from "./familyAppointmentCancelPopup";
import jsPDF from "jspdf";
import tmplLogo from "../images/tmplLogo.png";
import "./hm-theme.css";

function EmpAppointments() {
  const { userid } = useParams();
  const [appointmentDetails, setAppointmentDetails] = useState([]);
  const [loader, setLoader] = useState(true);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const navigate = useNavigate();
  const token = JSON.parse(localStorage.getItem("data"))?.token;
  const userRole = JSON.parse(localStorage.getItem("data"))?.role;

  useEffect(() => {
    if (searchTerm.trim()) {
      searchData(0);  
    } else {
      getData(currentPage);   
    }
  }, [searchTerm]); 

  const searchData = async () => {
    setLoader(true);
    try {
      const searchUrl = `/api/employeeappointment/userid=${userid}`;
      const response = await axios.get(searchUrl, {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      setAppointmentDetails(response.data);
      setLoader(false);
    } catch (error) {
      setLoader(false);
      handleError(error);
    }
  };

  const getData = async (pageNumber = 0) => {
    try {
      setLoader(true);
      const trans = await axios.get(`/api/employeeappointment/getAllAppointmentsByUserid/userid=${userid}`, {
        params: { page: pageNumber },
        headers: { Authorization: `Bearer ${token}` },
      });

      const appointments = trans.data.content;
      setAppointmentDetails(appointments);
      setCurrentPage(trans.data.number);
      setTotalPages(trans.data.totalPages);
      setLoader(false);
    } catch (error) {
      setLoader(false);
      handleError(error);
    }
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
    if (searchTerm.trim()) {
      searchData(newPage);
    } else {
      getData(newPage);
    }
  };

  function handleError(error) {
    if (error.response) {
      if (error.response.status === 401) {
        navigate(`/signin`);
        toast.error("Your session has expired. Please login.");
      } else if (error.response.data && error.response.data.message) {
        toast.error(error.response.data.message);
      } else if (error.response.data && error.response.data.code) {
        toast.error(error.response.data.code);
      } else {
        toast.error("An error occurred.");
      }
    } else if (error.message) {
      toast.error(error.message);
    } else {
      toast.error("An error occurred.");
    }
  }

  const handleViewReport = (reportId) => {
    const url = `/api/employee-appointment-reports/view/${reportId}`;
    axios({
      url,
      method: "GET",
      responseType: "blob",
      headers: { Authorization: `Bearer ${token}` },
    }).then((response) => {
      const contentType = response.headers["content-type"] || "application/octet-stream";
      const blobUrl = window.URL.createObjectURL(new Blob([response.data], { type: contentType }));
      window.open(blobUrl, "_blank");
    }).catch(() => toast.error("Failed to view report."));
  };

  const handleDownloadReport = (reportId, fileName) => {
    axios({
      url: `/api/employee-appointment-reports/view/${reportId}`,
      method: "GET",
      responseType: "blob",
      headers: { Authorization: `Bearer ${token}` },
    }).then((response) => {
      const contentType = response.headers["content-type"] || "application/octet-stream";
      const blobUrl = window.URL.createObjectURL(new Blob([response.data], { type: contentType }));
      const link = document.createElement("a");
      link.href = blobUrl;
      link.setAttribute("download", fileName || "report");
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    }).catch(() => toast.error("Failed to download report."));
  };

  const handleBack = () => {
    navigate(-1);
  };

  const [loading, setLoading] = useState(null);
  function rsubmit(e, id, status) {
    e.preventDefault();
    setLoading(id);
  
    const tok = JSON.parse(localStorage.getItem("data"))?.token;
    const entitiesToUpdate = ({ id, reportstatus: status });
  
    axios.post("/api/employeeappointment/approve", entitiesToUpdate, { headers: { Authorization: `Bearer ${tok}` } })
      .then((response) => {
        setLoading(null);
        getData();
      })
      .catch((error) => {
        handleError(error);
      });
  }

  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [selectedAppointmentId, setSelectedAppointmentId] = useState(null);

  const handleUploadReport = (appointmentId) => {
    setSelectedAppointmentId(appointmentId);
    setIsUploadModalOpen(true);
  };

  const handleCloseUploadModal = () => {
    setIsUploadModalOpen(false);
    getData();
  };

  const [isTestsModalOpen, setIsTestsModalOpen] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const openTestsModal = (pkg) => {
    setSelectedPackage(pkg);
    setIsTestsModalOpen(true);
  };

  const closeTestsModal = () => {
    setSelectedPackage(null);
    setIsTestsModalOpen(false);
  };

  const handleRetry = async (appointment) => {
    setLoader(true); 
    const tok = JSON.parse(localStorage.getItem('data'))?.token;
    try {
      await axios.post('/api/employeeappointment/re-trigger-api', appointment, {
        headers: { Authorization: `Bearer ${tok}` },
      });
      toast.success('Retried successfully');
    } catch (error) {
      toast.error('Retry failed');
    } finally {
      setLoader(false); 
    }
  };

  const handleFetchReport = async (appointment) => {
    setLoader(true); 
    const tok = JSON.parse(localStorage.getItem('data'))?.token;
    try {
      await axios.post('/api/employeeappointment/fetchReport', appointment, {
        headers: { Authorization: `Bearer ${tok}` },
      });
      toast.success('Report fetched successfully');
      getData();
    } catch (error) {
      handleError(error);
    } finally {
      setLoader(false); 
    }
  };

  const renderPageNumbers = () => {
    const pageNumbers = [];
    const maxPageNumbersToShow = 5;
    let startPage = Math.max(0, currentPage - 2);
    let endPage = Math.min(totalPages - 1, currentPage + 2);

    if (currentPage <= 2) {
      endPage = Math.min(totalPages - 1, maxPageNumbersToShow - 1);
    }

    if (currentPage >= totalPages - 3) {
      startPage = Math.max(0, totalPages - maxPageNumbersToShow);
    }

    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(
        <Pagination.Item key={i} active={i === currentPage} onClick={() => handlePageChange(i)}>
          {i + 1}
        </Pagination.Item>
      );
    }
    return pageNumbers;
  };

  const [showImageModal, setShowImageModal] = useState(false);
  const handleBookApp = () => setShowImageModal(true);
  const handleCloseModal = () => setShowImageModal(false);

  const [isCancelPopupOpen, setIsCancelPopupOpen] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState(null);

  const handleCancelAppointment = async (appointment) => {
    setSelectedAppointment(appointment);
    setIsCancelPopupOpen(true);
  };
  
  const handleCloseCancelPopup = () => {
    setIsCancelPopupOpen(false);
    getData();
  };

  const handleAcceptAppointment = async (appointmentId) => {
    const tok = JSON.parse(localStorage.getItem("data"))?.token;
    setLoader(true);
    try {
      await axios.post(
        "/api/employeeappointment/accept-or-reschedule",
        { id: `${appointmentId}`, appointmentstatus: "APPOINTMENT CONFIRMED" },
        { headers: { Authorization: `Bearer ${tok}` } }
      );
      toast.success("Employee Appointment Confirmed successfully.");
      getData();
      setLoader(false);
    } catch (error) {
      setLoader(false);
      handleError(error);
    }
  };

  const [isReschedulePopupOpen, setIsReschedulePopupOpen] = useState(false);  
  const handleRescheduleAppointment = (appointmentId) => {
    setSelectedAppointmentId(appointmentId);
    setIsReschedulePopupOpen(true);
  };

  const handleCloseReschedulePopup = () => {
    setIsReschedulePopupOpen(false);
    getData();
  };

  const handleDownloadReciept = (uid, paymentId) => {
    navigate('/users/payment-receipt', { 
      state: { userid: uid, paymentId } 
    });
  };

  const getStatusBadgeClass = (status) => {
    if (!status) return "hm-badge hm-badge-warning";
    if (status.includes("CANCELLED")) return "hm-badge hm-badge-danger";
    if (status.includes("COMPLETED")) return "hm-badge hm-badge-success";
    if (status.includes("CONFIRMED")) return "hm-badge hm-badge-info";
    return "hm-badge hm-badge-warning";
  };

  return (
    <div>
      {/* Section Header */}
      <div className="hm-card-header mb-3" style={{ background: "transparent", border: "none", padding: "0 0 0.5rem 0" }}>
        <h4 className="hm-card-title">
          <i className="material-icons">event</i>
          Lab Appointments
        </h4>
        <div className="d-flex align-items-center gap-2">
          <button className="btn btn-outline-secondary btn-sm rounded-pill px-3" onClick={handleBack}>
            ◄ Back
          </button>
          <button className="btn-hm-primary" onClick={handleBookApp} type="button">
            <i className="material-icons" style={{ fontSize: "1rem" }}>add</i>
            Book Appointment
          </button>
        </div>
      </div>

      {/* Table Card */}
      <div className="hm-card">
        <div className="hm-card-body p-0">
          <div style={{ overflowX: "auto" }}>
            <table className="hm-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Employee</th>
                  <th>Date</th>
                  <th>Lab</th>
                  <th>Contact</th>
                  <th>Package / Tests</th>
                  <th>Status</th>
                  <th>Report</th>
                  <th>Receipt</th>
                  <th>Lab Order</th>
                </tr>
              </thead>
              <tbody>
                {appointmentDetails.length === 0 ? (
                  <tr>
                    <td colSpan="10">
                      <div className="hm-empty-state">
                        <i className="material-icons">event_busy</i>
                        <p>No appointments found</p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  appointmentDetails.map((appointment, index) => (
                    <tr key={appointment.id}>
                      <td>{index + 1}</td>

                      {/* Employee Name - clickable to upload report if applicable */}
                      <td
                        className={
                          appointment.appStatus?.appointmentStatus !== 'APPOINTMENT CANCELLED' &&
                          appointment.appStatus?.appointmentStatus !== 'APPOINTMENT COMPLETED'
                            ? "text-primary fw-semibold cursor-pointer"
                            : "fw-semibold"
                        }
                        style={{
                          cursor: appointment.appStatus?.appointmentStatus !== 'APPOINTMENT CANCELLED' &&
                            appointment.appStatus?.appointmentStatus !== 'APPOINTMENT COMPLETED' ? "pointer" : "default"
                        }}
                        title={
                          appointment.appStatus?.appointmentStatus !== 'APPOINTMENT CANCELLED' &&
                          appointment.appStatus?.appointmentStatus !== 'APPOINTMENT COMPLETED'
                            ? "Upload Report"
                            : ""
                        }
                        onClick={() => {
                          if (
                            appointment.appStatus?.appointmentStatus !== 'APPOINTMENT CANCELLED' &&
                            appointment.appStatus?.appointmentStatus !== 'APPOINTMENT COMPLETED'
                          ) {
                            handleUploadReport(appointment.id);
                          }
                        }}
                      >
                        {appointment.emplo?.name} {appointment.emplo?.lastname}
                      </td>

                      <td>{appointment.date}</td>
                      <td className="fw-semibold">{appointment.labs?.labname}</td>
                      <td>{appointment.emplo?.phoneno}</td>

                      {/* Package & Tests */}
                      <td>
                        {appointment.pack !== null && (
                          <div className="d-flex align-items-center gap-2 mb-1">
                            <span className="badge bg-primary bg-opacity-10 text-primary border border-primary border-opacity-25 px-2 py-1 rounded-pill small">
                              {appointment.pack?.packagename}
                            </span>
                            <FiInfo
                              onClick={() => openTestsModal(appointment.packageid)}
                              className="text-primary cursor-pointer"
                              style={{ cursor: 'pointer', fontSize: '14px' }}
                            />
                          </div>
                        )}
                        {appointment.additionalTest && appointment.additionalTest.length > 0 && (
                          <div>
                            <span className="text-muted small d-block mb-1">Additional Tests:</span>
                            <ul className="list-unstyled mb-0 ps-2">
                              {appointment.additionalTest.map((test, i) => (
                                <li key={i} className="text-muted small">• {test.testDetails?.testname}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </td>

                      {/* Status + Action Buttons */}
                      <td>
                        <span className={getStatusBadgeClass(appointment.appStatus?.appointmentStatus)}>
                          {appointment.appStatus?.appointmentStatus}
                        </span>

                        {appointment.appStatus?.appointmentStatus !== 'APPOINTMENT CANCELLED' &&
                          appointment.appStatus?.appointmentStatus !== 'APPOINTMENT COMPLETED' && (
                          <div className="d-flex flex-wrap gap-1 mt-2">
                            {appointment.appStatus?.appointmentStatus === 'APPOINTMENT RESCHEDULED' &&
                              appointment.appStatus?.updatedBy !== 'Employee' && (
                              <button
                                className="btn btn-success btn-sm rounded-pill px-2"
                                onClick={() => handleAcceptAppointment(appointment.id)}
                              >Accept</button>
                            )}
                            <button
                              className="btn btn-outline-danger btn-sm rounded-pill px-2"
                              onClick={() => handleCancelAppointment(appointment)}
                            >Cancel</button>
                            <button
                              className="btn btn-outline-warning btn-sm rounded-pill px-2"
                              onClick={() => handleRescheduleAppointment(appointment.id)}
                            >Reschedule</button>
                          </div>
                        )}
                      </td>

                      {/* Report */}
                      <td>
                        {appointment.orderStatus ? (
                          appointment.orderStatus?.appointmentReport ? (
                            <a
                              href={appointment.orderStatus.appointmentReport}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="hm-badge hm-badge-success text-decoration-none"
                            >View</a>
                          ) : (
                            <button
                              className="btn btn-outline-primary btn-sm rounded-pill px-2"
                              onClick={() => handleFetchReport(appointment)}
                            >Fetch Report</button>
                          )
                        ) : appointment.reportstatus === 'APPROVED' ? (
                          <>
                            {(appointment.reports || []).length > 0 ? (
                              <ul className="list-unstyled mb-1">
                                {(appointment.reports || []).map((report, ri) => (
                                  <li key={report.id} className="d-flex align-items-center gap-2 mb-1">
                                    <span
                                      className="text-primary small cursor-pointer"
                                      style={{ cursor: 'pointer', textDecoration: 'underline' }}
                                      onClick={() => handleViewReport(report.id)}
                                      title={report.fileName || `File ${ri + 1}`}
                                    >
                                      {report.fileName || `File ${ri + 1}`}
                                    </span>
                                    <FaDownload
                                      size={12}
                                      style={{ cursor: 'pointer', color: '#198754' }}
                                      title="Download"
                                      onClick={() => handleDownloadReport(report.id, report.fileName)}
                                    />
                                  </li>
                                ))}
                              </ul>
                            ) : (
                              <span className="text-muted small">No files</span>
                            )}
                            {(userRole === "ROLE_ADMIN" || userRole === "ROLE_SUPER") && (
                              <FaEdit
                                className="text-secondary cursor-pointer"
                                size={14}
                                onClick={() => handleUploadReport(appointment.id)}
                                title="Modify Report"
                                style={{ cursor: "pointer" }}
                              />
                            )}
                          </>
                        ) : appointment.reportstatus === "REQUESTED" ? (
                          <div className="d-flex align-items-center gap-2">
                            <span className="hm-badge hm-badge-warning">Pending</span>
                            {(userRole === "ROLE_ADMIN" || userRole === "ROLE_SUPER") && (
                              <>
                                <BiSolidCheckCircle
                                  style={{ color: "green", cursor: "pointer", fontSize: "1.2rem" }}
                                  title="Approve"
                                  onClick={(e) => rsubmit(e, appointment.id, "APPROVED")}
                                />
                                <BiSolidXCircle
                                  style={{ color: "red", cursor: "pointer", fontSize: "1.2rem" }}
                                  title="Reject"
                                  onClick={(e) => rsubmit(e, appointment.id, "REJECTED")}
                                />
                              </>
                            )}
                          </div>
                        ) : (
                          <span className="text-muted small">{appointment.reportstatus || "No Report"}</span>
                        )}
                      </td>

                      {/* Receipt */}
                      <td
                        style={{
                          cursor: appointment.paymentId !== null ? "pointer" : "default",
                          textAlign: "center",
                          verticalAlign: "middle",
                        }}
                        title={appointment.paymentId !== null ? "Download Receipt" : ""}
                        onClick={() => appointment.paymentId !== null && handleDownloadReciept(appointment.userid, appointment.paymentId)}
                      >
                        {appointment.paymentId !== null ? (
                          <FaDownload className="text-success" />
                        ) : (
                          <span className="text-muted small">N/A</span>
                        )}
                      </td>

                      {/* Lab Order Status */}
                      <td>
                        <div className="fw-semibold small">{appointment.orderStatus?.status}</div>
                        {appointment.orderStatus ? (
                          (appointment.orderStatus?.orderId === "" || appointment.orderStatus?.orderId == null) &&
                          appointment.appointmentstatus !== 'APPOINTMENT CANCELLED' ? (
                            <button
                              className="btn btn-outline-danger btn-sm rounded-pill mt-1 px-2 d-flex align-items-center gap-1"
                              onClick={() => handleRetry(appointment)}
                            >
                              Retry <FaRedoAlt size={10} />
                            </button>
                          ) : (
                            <div className="text-muted small">{appointment.orderStatus?.orderId}</div>
                          )
                        ) : (
                          <span className="text-muted small">N/A</span>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div className="d-flex justify-content-center py-3">
              <Pagination>
                <Pagination.First onClick={() => handlePageChange(0)} disabled={currentPage === 0} />
                <Pagination.Prev onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 0} />
                {renderPageNumbers()}
                <Pagination.Next onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages - 1} />
                <Pagination.Last onClick={() => handlePageChange(totalPages - 1)} disabled={currentPage === totalPages - 1} />
              </Pagination>
            </div>
          )}
        </div>

        {isUploadModalOpen && (
          <UploadReport
            isOpen={isUploadModalOpen}
            onRequestClose={handleCloseUploadModal}
            empId={selectedAppointmentId}
          />
        )}

        {showImageModal && (
          <DisclaimerPopup isOpen={showImageModal} onRequestClose={handleCloseModal} userid={userid} />
        )}

        {isReschedulePopupOpen && (
          <AppointmentReschedule
            isOpen={isReschedulePopupOpen}
            onRequestClose={handleCloseReschedulePopup}
            appointmentId={selectedAppointmentId}
          />
        )}

        {isCancelPopupOpen && (
          <FamilyAppointmentCancel
            isOpen={isCancelPopupOpen}
            onRequestClose={handleCloseCancelPopup}
            appointment={selectedAppointment}
          />
        )}

        {isTestsModalOpen && (
          <TestForPackage
            isOpen={isTestsModalOpen}
            onRequestClose={closeTestsModal}
            packageId={selectedPackage}
          />
        )}

        {loader && <div className="loading"></div>}
      </div>
    </div>
  );
}

export default EmpAppointments;
